import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Undo2,
  Redo2,
} from "lucide-react";

const ToolbarBtn = ({ icon: Icon, onClick, label, active = false }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${
      active
        ? "bg-paper-2 text-forest"
        : "text-ink-soft hover:bg-paper-2 hover:text-ink"
    }`}
  >
    <Icon size={14} />
  </button>
);

const exec = (cmd, val = null) => {
  document.execCommand(cmd, false, val);
};

export default function RichTextEditor({
  initialHtml,
  onChange,
  placeholder = "Comienza tu capítulo aquí…",
  testid = "rich-text-editor",
  templateStyle = null,
}) {
  const ref = useRef(null);
  const [, force] = useState(0);

  // Set initial content only once (uncontrolled afterwards to preserve caret).
  // Sanitize via DOMPurify to prevent XSS from any HTML source (server, paste,
  // migrations). Allow the tags/attrs the toolbar can produce.
  useEffect(() => {
    if (!ref.current) return;
    const clean = DOMPurify.sanitize(initialHtml || "", {
      ALLOWED_TAGS: [
        "p", "br", "strong", "em", "u", "b", "i",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "ul", "ol", "li", "blockquote", "span", "div",
        "a",
      ],
      ALLOWED_ATTR: ["href", "title", "target", "rel"],
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    });
    if (ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml]);

  const handleInput = () => {
    const html = ref.current?.innerHTML || "";
    const text = ref.current?.innerText || "";
    onChange?.({ html, text });
    force((n) => n + 1);
  };

  const fmt = (cmd, val) => {
    ref.current?.focus();
    exec(cmd, val);
    handleInput();
  };

  const wordCount = (ref.current?.innerText || "").trim().split(/\s+/).filter(Boolean).length;
  const charCount = (ref.current?.innerText || "").length;
  const empty = charCount === 0;

  // Apply template-driven page restrictions
  const ts = templateStyle || {};
  const fontFamily = ts.font_family || null;
  const fontSize = ts.font_size_pt ? `${ts.font_size_pt}pt` : null;
  const lineHeight = ts.line_spacing || null;
  const padTop = ts.margin_top_cm ? `${ts.margin_top_cm}cm` : undefined;
  const padBottom = ts.margin_bottom_cm ? `${ts.margin_bottom_cm}cm` : undefined;
  const padLeft = ts.margin_left_cm ? `${ts.margin_left_cm}cm` : undefined;
  const padRight = ts.margin_right_cm ? `${ts.margin_right_cm}cm` : undefined;

  const canvasStyle = {
    ...(fontFamily ? { fontFamily: `${fontFamily}, Cormorant Garamond, Georgia, serif` } : {}),
    ...(fontSize ? { fontSize } : {}),
    ...(lineHeight ? { lineHeight: String(lineHeight) } : {}),
    ...(padTop ? { paddingTop: padTop } : {}),
    ...(padBottom ? { paddingBottom: padBottom } : {}),
    ...(padLeft ? { paddingLeft: padLeft } : {}),
    ...(padRight ? { paddingRight: padRight } : {}),
  };

  return (
    <div className="flex flex-col h-full" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-2 border-b border-rule bg-white sticky top-0 z-10">
        <div className="flex items-center gap-0.5 pr-1.5 border-r border-rule">
          <ToolbarBtn icon={Heading1} label="Título grande" onClick={() => fmt("formatBlock", "H2")} />
          <ToolbarBtn icon={Heading2} label="Subtítulo" onClick={() => fmt("formatBlock", "H3")} />
        </div>
        <div className="flex items-center gap-0.5 px-1.5 border-r border-rule">
          <ToolbarBtn icon={Bold} label="Negrita" onClick={() => fmt("bold")} />
          <ToolbarBtn icon={Italic} label="Cursiva" onClick={() => fmt("italic")} />
          <ToolbarBtn icon={Underline} label="Subrayado" onClick={() => fmt("underline")} />
        </div>
        <div className="flex items-center gap-0.5 px-1.5 border-r border-rule">
          <ToolbarBtn icon={List} label="Lista" onClick={() => fmt("insertUnorderedList")} />
          <ToolbarBtn icon={ListOrdered} label="Lista numerada" onClick={() => fmt("insertOrderedList")} />
          <ToolbarBtn icon={Quote} label="Cita" onClick={() => fmt("formatBlock", "BLOCKQUOTE")} />
        </div>
        <div className="flex items-center gap-0.5 pl-1.5">
          <ToolbarBtn icon={Undo2} label="Deshacer" onClick={() => fmt("undo")} />
          <ToolbarBtn icon={Redo2} label="Rehacer" onClick={() => fmt("redo")} />
        </div>
        <div className="ml-auto text-[10.5px] text-ink-soft tracking-wide pr-2 hidden sm:flex items-center gap-2">
          {templateStyle?.university_name && (
            <>
              <span className="inline-flex items-center gap-1 text-copper font-medium">
                {templateStyle.university_name.split("—")[0].trim()} · {templateStyle.citation_style}
              </span>
              <span className="text-rule">|</span>
            </>
          )}
          <span>{wordCount} palabras · {charCount} caracteres</span>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative flex-1 overflow-y-auto bg-white">
        {empty && (
          <p className="pointer-events-none absolute top-7 left-9 right-9 font-serif italic text-ink-soft/70 text-[15px]">
            {placeholder}
          </p>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          spellCheck
          data-testid="editor-canvas"
          style={canvasStyle}
          className="prose prose-sm max-w-none px-9 py-7 font-serif text-[15px] leading-[1.75] text-ink/90 focus:outline-none min-h-full
            [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-ink
            [&_h3]:font-serif [&_h3]:text-xl [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-ink
            [&_p]:my-3
            [&_blockquote]:border-l-4 [&_blockquote]:border-copper [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-ink-soft
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3
            [&_strong]:text-ink [&_em]:text-ink"
        />
      </div>
    </div>
  );
}
